import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@kognitos/lattice';

import type { Bdkv2BookDescriptor } from '@/generated/hooks/bdk/models';
import { useAutomationSendMessage } from '@/generated/hooks/rain/v1/automation_scoped/thread_service';
import { RAIN_QUILL_AGENT_ID } from '@/modules/rain/agents/quill';
import { buildRainUserMessage } from '@/modules/rain/utils/message';
import { captureException } from '@/modules/sentry';
import { errorHandler } from '@/shared/utils/error-handler';
import { getIntegrationDisplayName } from '@/shared/utils/integrations-utils';

interface IUseAddIntegrationToChatParams {
  organizationId: string;
  workspaceId: string;
  automationId?: string;
  threadId?: string | null;
}

/**
 * Returns a callback that asks Quill, in the draft's Rain thread, to add an
 * integration to the automation. It does nothing until both the automation and
 * the thread are known, and shows an error toast if the message fails to send.
 */
export function useAddIntegrationToChat({
  organizationId,
  workspaceId,
  automationId,
  threadId,
}: IUseAddIntegrationToChatParams) {
  const { t: tAutomation } = useTranslation('automation');
  const { mutateAsync: sendRainMessageAsync } = useAutomationSendMessage();

  return useCallback(
    (integration: Bdkv2BookDescriptor) => {
      if (!threadId || !automationId) return;
      const prompt = tAutomation('automation.draftRail.addIntegrationPrompt', {
        name: getIntegrationDisplayName(integration),
      });
      sendRainMessageAsync({
        organization: organizationId,
        workspace: workspaceId,
        automation: automationId,
        agentId: RAIN_QUILL_AGENT_ID,
        threadId,
        data: { message: buildRainUserMessage(prompt) },
      }).catch((error: unknown) => {
        errorHandler(error, () => {
          toast.error(tAutomation('automation.draftRail.addIntegrationError'));
          captureException(
            error instanceof Error ? error : new Error(String(error)),
            {
              tags: { component: 'AutomationEditor', action: 'addIntegration' },
            },
          );
        });
      });
    },
    [
      automationId,
      organizationId,
      workspaceId,
      threadId,
      sendRainMessageAsync,
      tAutomation,
    ],
  );
}
